import api, { authHeaders } from '../apiService';

export type ClassificationContext = {
  actor_uid: string;
  owner_identity: string;
  company_slug: string;
  project_id: string;
  is_project_owner?: boolean;
  transport?: { protocol: 'bark-classification-v1' };
  deal_scope?: { share_id: string; root_contract_id: string; revision: string | null;
    access: 'edit' | 'owner'; can_edit: boolean; can_manage_sharing: boolean };
};

export function selectedDealShare(search: string): string | undefined {
  const values = new URLSearchParams(search).getAll('shared_deal');
  if (!values.length) return undefined;
  if (values.length !== 1 || !/^cs_[a-f0-9]{32}$/.test(values[0])) throw new Error('This shared deal link is invalid.');
  return values[0];
}

export function classificationApiPrefix(context: ClassificationContext): string {
  return context.deal_scope ? `/classification/shared-deals/${encodeURIComponent(context.deal_scope.share_id)}/api`
    : `/classification/projects/${encodeURIComponent(context.project_id)}/api`;
}

export function classificationContextScope(context: ClassificationContext): unknown[] {
  const project = [context.actor_uid, context.owner_identity, context.company_slug, context.project_id];
  // Preserve existing project retry keys across this rollout.
  return context.deal_scope ? [...project, context.deal_scope.share_id, context.deal_scope.root_contract_id] : project;
}

export function classificationParentPath(context: ClassificationContext, selection: string): string {
  const params = new URLSearchParams(selection);
  params.delete('shared_deal');
  if (context.deal_scope) params.set('shared_deal', context.deal_scope.share_id);
  return `/classification/${encodeURIComponent(context.project_id)}${params.toString() ? `?${params}` : ''}`;
}

export class ClassificationRequestError extends Error {
  constructor(public status: number, sharedDeal = false) {
    super(status === 401 ? 'Your session has expired. Sign in again.'
      : status === 403 ? sharedDeal ? 'Access to this shared deal has been removed or is unavailable.' : 'Classification is not enabled or this project is unavailable to your account.'
      : 'Classification could not be loaded. Try again.');
  }
}

export async function classificationBase(): Promise<string> {
  await api.initialize();
  const base = api.getReportLibraryUrl().replace(/\/+$/, '');
  if (!base) throw new Error('Classification is unavailable in this environment.');
  return base;
}

export async function classificationRequest(path: string, signal: AbortSignal): Promise<Response> {
  const base = await classificationBase();
  const headers = await authHeaders();
  if (!headers.Authorization) throw new ClassificationRequestError(401);
  const response = await fetch(`${base}/classification${path}`, {
    headers, signal, credentials: 'omit', cache: 'no-store', redirect: 'error',
  });
  if (!response.ok) throw new ClassificationRequestError(response.status, path.startsWith('/shared-deals/') || path.includes('/sharing'));
  return response;
}

export async function readClassificationContext(projectId: string, signal: AbortSignal, shareId?: string): Promise<ClassificationContext> {
  const response = await classificationRequest(shareId ? `/shared-deals/${encodeURIComponent(shareId)}/context`
    : `/projects/${encodeURIComponent(projectId)}/context`, signal);
  return checkedClassificationContext(await response.json(), projectId, shareId);
}

function checkedClassificationContext(context: ClassificationContext, projectId: string, shareId?: string): ClassificationContext {
  if (!context || typeof context !== 'object' || context.project_id !== projectId || (!shareId && context.deal_scope)
    || !(['actor_uid', 'owner_identity', 'company_slug'] as const).every(key => typeof context[key] === 'string' && context[key])) {
    throw new Error('Classification returned an invalid project context.');
  }
  if (shareId && (context.deal_scope?.share_id !== shareId || !/^[a-f0-9]{32}$/.test(context.deal_scope?.root_contract_id || '')
    || !['edit', 'owner'].includes(context.deal_scope?.access) || context.deal_scope?.can_edit !== true
    || typeof context.deal_scope?.can_manage_sharing !== 'boolean'
    || !/^[a-f0-9]{32}$/.test(context.deal_scope?.revision || ''))) throw new Error('The shared deal context could not be verified.');
  return context;
}

export async function readClassificationRegister(projectId: string, signal: AbortSignal, shareId?: string) {
  const response = await classificationRequest(shareId ? `/shared-deals/${encodeURIComponent(shareId)}/register`
    : `/projects/${encodeURIComponent(projectId)}/register`, signal);
  const data = await response.json();
  const context = checkedClassificationContext(data.context, projectId, shareId);
  if (!Array.isArray(data.projects)) throw new Error('Classification register metadata is invalid.');
  return { context, projects: data.projects as unknown[] };
}
